/**
 * Internationalised formatting helpers. Currency and dates always go through
 * `Intl` so we never mislabel amounts (e.g. showing "$" for EUR receipts).
 */

const DEFAULT_LOCALE =
  (typeof navigator !== 'undefined' && navigator.language) || 'en-GB';

const DEFAULT_CURRENCY = 'EUR';

/**
 * Format a numeric amount as currency. When the currency code is unknown we
 * fall back to a plain decimal so we never invent a wrong currency symbol.
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency?: string | null,
  locale: string = DEFAULT_LOCALE
): string {
  const value = Number.isFinite(amount as number) ? (amount as number) : 0;
  const code = normaliseCurrency(currency);
  if (!code) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
    }).format(value);
  } catch {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
}

/**
 * Format cents as currency (see {@link formatCurrency}).
 */
export function formatCentsAsCurrency(
  cents: number,
  currency?: string | null,
  locale: string = DEFAULT_LOCALE
): string {
  return formatCurrency(cents / 100, currency, locale);
}

function normaliseCurrency(currency?: string | null): string | null {
  if (!currency) {
    return DEFAULT_CURRENCY;
  }
  const trimmed = currency.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(trimmed) ? trimmed : null;
}

export function formatDate(
  value: string | number | Date | null | undefined,
  locale: string = DEFAULT_LOCALE
): string {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatDateTime(
  value: string | number | Date | null | undefined,
  locale: string = DEFAULT_LOCALE
): string {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/**
 * Human readable "resets in ..." helper for the daily scan quota. The reset
 * instant is a UTC midnight boundary provided by the backend.
 */
export function formatResetRelative(resetAt: string | null | undefined): string {
  if (!resetAt) {
    return '';
  }
  const target = new Date(resetAt);
  if (Number.isNaN(target.getTime())) {
    return '';
  }
  const diffMs = target.getTime() - Date.now();
  if (diffMs <= 0) {
    return 'now';
  }
  const totalMinutes = Math.round(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours >= 1) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

export function formatQuantity(
  quantity: number,
  locale: string = DEFAULT_LOCALE
): string {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 3,
  }).format(quantity);
}
